import { HugeiconsIcon } from "@hugeicons/react";
import { RefreshIcon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFilteredBillingRows, useSmsBillingActions } from "../hooks/useSmsBilling";
import { useSmsBillingStore } from "../stores/smsBillingStore";
import { formatAmount, formatCount } from "../utils/format";
import { formatMonthLabel } from "../utils/month";
import type { SmsBillingRow, SmsMonthlyBilling } from "../types/smsBilling.types";

const COLUMNS = [
  { key: "companyName", label: "업체명", align: "left" },
  { key: "billMonth", label: "청구월", align: "left" },
  { key: "smsCount", label: "단문 건수", align: "right" },
  { key: "smsAmount", label: "단문 비용 (P)", align: "right" },
  { key: "lmsCount", label: "장문 건수", align: "right" },
  { key: "lmsAmount", label: "장문 비용 (P)", align: "right" },
  { key: "totalCount", label: "총 건수", align: "right" },
  { key: "totalAmount", label: "총 비용 (P)", align: "right" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

const cellAlign = (align: "left" | "right") =>
  align === "right" ? "text-right tabular-nums" : "text-left";

const renderCell = (row: SmsBillingRow, key: ColumnKey) => {
  switch (key) {
    case "companyName":
      return <span className="font-medium text-gray-900">{row.companyName || "-"}</span>;
    case "billMonth":
      return formatMonthLabel(row.billMonth);
    case "smsCount":
      return formatCount(row.smsCount);
    case "lmsCount":
      return formatCount(row.lmsCount);
    case "totalCount":
      return formatCount(row.totalCount);
    case "smsAmount":
      return formatAmount(row.smsAmount);
    case "lmsAmount":
      return formatAmount(row.lmsAmount);
    case "totalAmount":
      return <span className="font-semibold text-gray-900">{formatAmount(row.totalAmount)}</span>;
  }
};

interface SmsBillingTableProps {
  data: SmsMonthlyBilling | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error?: Error | null;
}

export function SmsBillingTable({ data, isLoading, isFetching, error }: SmsBillingTableProps) {
  const appliedRange = useSmsBillingStore((s) => s.appliedRange);
  const keyword = useSmsBillingStore((s) => s.keyword);
  const setKeyword = useSmsBillingStore((s) => s.setKeyword);
  const { refresh } = useSmsBillingActions();
  const { rows, totals } = useFilteredBillingRows(data?.rows);

  const hasRows = rows.length > 0;
  const rangeLabel = appliedRange
    ? `${formatMonthLabel(appliedRange.fromMonth)} ~ ${formatMonthLabel(appliedRange.toMonth)}`
    : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white p-3">
        <span className="text-base font-medium text-gray-900">업체별 월간 청구 내역</span>
        {rangeLabel && <span className="text-xs text-gray-500">{rangeLabel}</span>}
        {data && (
          <span className="text-xs text-gray-500">
            {rows.length}행
            {keyword.trim() && ` (전체 ${data.rows.length}행 중)`}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="업체명 검색"
            disabled={!data}
            className="h-7 w-56 text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!appliedRange || isFetching}
            onClick={refresh}
            className="h-7 px-2.5 text-xs"
          >
            <HugeiconsIcon
              icon={RefreshIcon}
              size={14}
              className={cn("mr-1", isFetching && "animate-spin")}
            />
            새로고침
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        <table className="w-full">
          <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "h-10 px-4 py-3 text-xs font-medium uppercase text-gray-500",
                    cellAlign(col.align),
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {!appliedRange ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 text-center text-xs text-gray-400">
                  조회월을 정하고 조회 버튼을 누르세요
                </td>
              </tr>
            ) : isLoading ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 text-center text-xs text-gray-400">
                  발송 기록을 집계하는 중... 기간이 길면 수십 초 걸릴 수 있습니다
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 px-4 text-center text-xs text-red-600">
                  <p className="font-medium">청구 내역을 불러오지 못했습니다</p>
                  <p className="mt-1 text-gray-500">{error.message}</p>
                </td>
              </tr>
            ) : !hasRows ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 text-center text-xs text-gray-400">
                  {keyword.trim() ? "검색 결과가 없습니다" : "해당 기간에 발송된 문자가 없습니다"}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={`${row.companyGuid}-${row.billMonth}`} className="h-10 hover:bg-gray-50">
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className={cn("px-4 py-2 text-xs text-gray-700", cellAlign(col.align))}
                    >
                      {renderCell(row, col.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {hasRows && (
            <tfoot className="sticky bottom-0 z-10 border-t border-gray-200 bg-gray-50">
              <tr className="h-10 text-xs font-semibold text-gray-900">
                <td className="px-4 py-2" colSpan={2}>
                  합계{keyword.trim() && " (검색 결과)"}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{formatCount(totals.smsCount)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatAmount(totals.smsAmount)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatCount(totals.lmsCount)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatAmount(totals.lmsAmount)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatCount(totals.totalCount)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatAmount(totals.totalAmount)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        {data
          ? `단문 ${formatAmount(data.smsUnitPrice)}P · 장문 ${formatAmount(data.lmsUnitPrice)}P · 본문 ${data.smsMaxBytes}byte(EUC-KR) 초과 시 장문 · 발송 성공 건만 집계`
          : "단문 8.4P · 장문 25.9P · 본문 90byte(EUC-KR) 초과 시 장문 · 발송 성공 건만 집계"}
      </div>
    </div>
  );
}
